import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";

import BranchDetailPage from "./BranchDetailPage";
import ReleasePledgedGoldLocationPage from "./ReleasePledgedGoldLocationPage";

const PRERENDER_DATA_ID = "__G1_PRERENDER_PAGE_DATA__";

const readPrerenderedPageData = (slug) => {
  if (typeof document === "undefined") {
    return null;
  }

  const node = document.getElementById(PRERENDER_DATA_ID);

  if (!node || node.dataset.slug !== slug) {
    return null;
  }

  try {
    return JSON.parse(node.textContent || "null");
  } catch (error) {
    console.error("Unable to read prerendered page data:", error);
    return null;
  }
};

const serializePrerenderedPageData = (data) =>
  JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");

const LoadingPage = () => (
  <div className="p-10 text-center">Loading...</div>
);

const TemporaryLoadError = () => (
  <>
    <Helmet>
      <meta name="robots" content="noindex, follow" />
      <meta name="googlebot" content="noindex, follow" />
    </Helmet>

    <div className="p-10 text-center">
      This page could not be loaded right now. Please refresh and try again.
    </div>
  </>
);

const NotFoundPage = () => (
  <>
    <Helmet>
      <title>Page Not Found | G1 Gold</title>
      <meta name="robots" content="noindex, follow" />
      <meta name="googlebot" content="noindex, follow" />
    </Helmet>

    <div className="p-10 text-center">
      <h1 className="text-2xl font-semibold">Page Not Found</h1>
      <p className="mt-3">The requested page is no longer available.</p>
    </div>
  </>
);

const DynamicLandingPage = () => {
  const { slug } = useParams();

  const initialData = readPrerenderedPageData(slug);

  const [pageState, setPageState] = useState(() =>
    initialData ? { slug, data: initialData } : null
  );
  const [loading, setLoading] = useState(() => !initialData);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const currentPageData =
    pageState?.slug === slug ? pageState.data : null;

  useEffect(() => {
    let active = true;

    /*
      On a prerendered production URL, React Snap has embedded the exact page
      JSON into the HTML. Reuse it for the first browser render so hydration
      matches the prerendered DOM and no client-side JSON request is required.
    */
    const embeddedData = readPrerenderedPageData(slug);

    if (embeddedData) {
      setPageState({ slug, data: embeddedData });
      setLoading(false);
      setNotFound(false);
      setLoadError(false);

      return () => {
        active = false;
      };
    }

    const loadPage = async () => {
      setPageState(null);
      setLoading(true);
      setNotFound(false);
      setLoadError(false);

      try {
        const response = await fetch(
          `${process.env.PUBLIC_URL}/data/branches/${encodeURIComponent(slug)}.json`
        );

        if (response.status === 404) {
          if (active) {
            setNotFound(true);
            setLoading(false);
          }
          return;
        }

        if (!response.ok) {
          throw new Error(
            `Unable to load landing page (${response.status}) for: ${slug}`
          );
        }

        const pageJson = await response.json();

        if (!active) return;

        setPageState({ slug, data: pageJson });
        setLoading(false);
      } catch (error) {
        console.error("Error loading landing page:", error);

        if (active) {
          // A network/server failure is not a 404. Keep it distinct so a
          // temporary fetch problem cannot create false "Page Not Found" HTML.
          setLoadError(true);
          setLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      active = false;
    };
  }, [slug]);

  if (loading) {
    return <LoadingPage />;
  }

  if (notFound) {
    return <NotFoundPage />;
  }

  if (loadError && !currentPageData) {
    return <TemporaryLoadError />;
  }

  if (!currentPageData) {
    return <TemporaryLoadError />;
  }

  const renderedPage =
    currentPageData.pageType === "releasePledgedGoldLocation" ? (
      <ReleasePledgedGoldLocationPage page={currentPageData} />
    ) : (
      <BranchDetailPage branch={currentPageData} />
    );

  return (
    <>
      {renderedPage}
      <script
        id={PRERENDER_DATA_ID}
        type="application/json"
        data-slug={slug}
        dangerouslySetInnerHTML={{
          __html: serializePrerenderedPageData(currentPageData),
        }}
      />
    </>
  );
};

export default DynamicLandingPage;
